import {
  find,
  isEmpty,
  set,
  sortBy,
  uniqBy,
} from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Trans, withI18n } from 'lingui-react';
import { scroller, Element } from 'react-scroll';
import { withTheme } from 'styled-components';
import { Column, Row, Container } from 'styled-components-reactive-grid';
import { QuickActionButton } from 'base-components';

// TODO: Find a way to handle this that removes cross component dependency!
// Perhaps a app wide component registry, as has been suggested for
// generalized favorites & overview pages.
import {
  ADD_NOTE,
} from 'containers/QuickActionBarContainer/constants';

import Expandable, { ExpandableHeader, ExpandableContent } from 'containers/Expandable';

import Divider from 'elements/Divider';
import Widget from 'elements/Widget';

import CaseNotesContainer from './CaseNotesContainer';
import ServiceRequestNotesContainer from './ServiceRequestNotesContainer';

import {
  COMPOSER_ERROR,
  COMPOSER_HIDDEN,
  COMPOSER_INVISIBLE,
  COMPOSER_NO_RECIPIENTS,
  COMPOSER_VISIBLE,
  MAX_RECIPIENTS,
  SCROLLABLE_COMPOSER_NAME,
} from './constants';

import NotesWidgetHeader from './NotesWidgetHeader';
import Note from './Note';
import NoteCreator from './NoteCreator';
import NotesOptionsBar from './NotesOptionsBar';
import NoNotes from './NoNotes';
import GhostNote from './GhostNote';

import messages from './messages';

import {
  buildRecipientOptions,
  selectedRecipientsforApi,
} from './utils';

function buildSelectedRecipients(selectedRecipients, noteRecipients) {
  const selectedRecipientsFromNote = noteRecipients.reduce((acc, group) =>
    acc.concat(
      group.options.map(recipient => ({ ...recipient, isInTree: true })),
    ), []);
  const allSelectedRecipients = selectedRecipients.concat(selectedRecipientsFromNote);
  return uniqBy(allSelectedRecipients, 'userId').slice(0, MAX_RECIPIENTS);
}

function generateNoteExpandKey(noteId) {
  return `noteExpanded-${noteId}`;
}

function getCurrentOption(options, value) {
  return find(options, opt => opt.value === value);
}

function sortNotes(notes, sortValue) {
  const ascendingCaseNotes = sortBy(notes, note => note.sentAt);
  if (sortValue === 'desc') {
    return ascendingCaseNotes.reverse();
  }
  return ascendingCaseNotes;
}

function filterUnreadNotes(notes) {
  return notes.filter(note => note.status === 'unread');
}

export class NotesWidget extends Component {
  static propTypes = {
    actionBarItemClicked: PropTypes.shape({
      buttonId: PropTypes.string,
      dropdownItemId: PropTypes.string,
      time: PropTypes.number,
    }),
    componentsExpanded: PropTypes.shape().isRequired,
    info: PropTypes.shape({
      id: PropTypes.string,
      unreadNotesCount: PropTypes.number,
    }).isRequired,
    notes: PropTypes.shape({
      requesting: PropTypes.bool.isRequired,
      error: PropTypes.object,
      data: PropTypes.arrayOf(
        PropTypes.object,
      ).isRequired,
    }).isRequired,
    availableRecipients: PropTypes.shape({
      requesting: PropTypes.bool.isRequired,
      error: PropTypes.object,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          group: PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
          user: PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
        }),
      ).isRequired,
    }).isRequired,
    clearAvailableRecipients: PropTypes.func.isRequired,
    i18n: PropTypes.shape({
      _: PropTypes.func.isRequired,
    }).isRequired,
    loadNotes: PropTypes.func.isRequired,
    loadAvailableRecipients: PropTypes.func.isRequired,
    postNote: PropTypes.func.isRequired,
    setNotes: PropTypes.func.isRequired,
    updateNote: PropTypes.func.isRequired,
    setComponentExpanded: PropTypes.func.isRequired,
    theme: PropTypes.shape({
      dimensions: PropTypes.shape({
        topNavHeightInPixel: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
    type: PropTypes.oneOf([
      undefined,
      'modal',
    ]),
    action: PropTypes.oneOf([
      undefined,
      'add_note',
    ]),
    scrollToUnreadNote: PropTypes.bool,
    updateScrollToUnreadNote: PropTypes.func.isRequired,
    currentUser: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }),
  };

  static defaultProps = {
    actionBarItemClicked: {},
    type: undefined,
    scrollToUnreadNote: false,
    action: undefined,
    currentUser: {
      email: '',
    },
  };

  state = {
    composerStatus: COMPOSER_INVISIBLE,
    notesExpanded: {},
    selectedRecipients: [],
    shouldFocusTextArea: false,
    selectedNoteValue: '',
    selectedNoteSortValue: 'asc',
    oldNotesCollapsed: false,
  };

  componentWillMount() {
    const {
      info: {
        id,
        notesCount,
      },
      type,
      action,
      updateScrollToUnreadNote,
      loadNotes,
    } = this.props;

    if (type === 'modal') {
      if (action === 'add_note') {
        this.setState({ composerStatus: COMPOSER_VISIBLE });
      } else {
        updateScrollToUnreadNote(true);
      }
    }

    if (id && notesCount) {
      loadNotes(id);
    }
  }

  componentWillReceiveProps({
    actionBarItemClicked,
    info: {
      id,
      notesCount,
    },
    notes,
    availableRecipients,
    setComponentExpanded,
    componentsExpanded,
  }) {
    if (!this.props.info.id && id && notesCount) {
      this.props.loadNotes(id);
    }

    if (!this.props.notes.data.length && notes.data.length > 8) {
      this.setState({ oldNotesCollapsed: true });
    }

    const recipientsChanging = availableRecipients !== this.props.availableRecipients;
    const requestInProgress = availableRecipients.requesting;
    if (this.state.hasRequestedRecipients && recipientsChanging && !requestInProgress) {
      const error = availableRecipients.error;
      const recipientData = availableRecipients.data;
      if (error || !recipientData) {
        this.setState({ composerStatus: COMPOSER_ERROR });
      } else if (isEmpty(recipientData)) {
        this.setState({ composerStatus: COMPOSER_NO_RECIPIENTS });
      } else {
        this.setState({ composerStatus: COMPOSER_VISIBLE });
      }
    }

    /* The logic here is complicated but required
      We should expand the component only when
      notesWidget was not expanded and button 'ADD NOTE' is just clicked.
      If notesWidget is expanded just before and 'ADD NOTE' is clicked,
      we call handleNoteClick which scrolls you to new note area which is ready to be written.
    */
    const buttonId = actionBarItemClicked && actionBarItemClicked.buttonId;
    const actionTime = actionBarItemClicked && actionBarItemClicked.time;
    const addingNote = buttonId === ADD_NOTE;
    const buttonWasJustClicked = (
      buttonId !== this.props.actionBarItemClicked.buttonId ||
      actionTime !== this.props.actionBarItemClicked.time
    );
    const notesWidgetExpanded = componentsExpanded && componentsExpanded['widget-notes'];
    const notesWidgetWasJustExpanded = (
      notesWidgetExpanded &&
      notesWidgetExpanded !== this.props.componentsExpanded['widget-notes']
    );
    const shouldExpandNotesWidget = !notesWidgetExpanded && buttonWasJustClicked;
    const shouldHandleNewNoteClick = notesWidgetExpanded && (
      buttonWasJustClicked || notesWidgetWasJustExpanded
    );
    if (addingNote) {
      if (shouldExpandNotesWidget) {
        setComponentExpanded('widget-notes', true);
      } else if (shouldHandleNewNoteClick) {
        this.handleNewNoteClick();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { type, action, notes: { requesting, data } } = this.props;
    /* When it's a modal to add a note from cases page */
    if (!requesting && prevProps.notes.requesting && data.length) {
      if (type === 'modal' && action === 'add_note') {
        /* right after the notes are loaded there are not yet rendered so we need wait a bit
          to scroll to note creator.
        */
        this.handleNewNoteClick();
        return;
      }
    }
    this.scrollToUnreadNote();
  }

  componentWillUnmount() {
    this.props.clearAvailableRecipients();
    this.props.setNotes();
    this.props.updateScrollToUnreadNote(false);
  }

  focusTextArea = (textarea) => {
    if (textarea && this.state.shouldFocusTextArea) {
      textarea.focus();
      this.setState({ shouldFocusTextArea: false });
    }
  }

  handleExpandNote = (id, expanded) => {
    const noteExpandKey = generateNoteExpandKey(id);
    this.setState({
      notesExpanded: {
        ...this.state.notesExpanded,
        [noteExpandKey]: expanded,
      },
    });
  }

  handleExpandOldNotes = () => {
    this.setState({ oldNotesCollapsed: false });
  }

  handleNewNoteCancel = (event) => {
    event.preventDefault();
    this.setState({
      composerStatus: COMPOSER_HIDDEN,
      selectedRecipients: [],
      shouldFocusTextArea: false,
    });
  }

  handleNewNoteClick = () => {
    const {
      info,
      loadAvailableRecipients,
    } = this.props;
    const {
      composerStatus,
      selectedRecipients,
    } = this.state;

    this.scrollToNoteCreator();

    if (composerStatus === COMPOSER_INVISIBLE || composerStatus === COMPOSER_ERROR) {
      loadAvailableRecipients(info.id);
      this.setState({ hasRequestedRecipients: true });
    }

    this.setState({ composerStatus: COMPOSER_VISIBLE }); // Batch state update makes this fine

    if (selectedRecipients.length) {
      this.setState({
        shouldFocusTextArea: true,
      });
    }
  }

  handleNoteCreate = (message) => {
    const { info, postNote } = this.props;
    const { selectedRecipients } = this.state;
    const recipients = selectedRecipientsforApi(selectedRecipients);
    const payload = {
      caseId: info.id,
      params: {
        recipients,
        message,
      },
    };
    this.setState({
      composerStatus: COMPOSER_HIDDEN,
      selectedRecipients: [],
      shouldFocusTextArea: false,
    });
    postNote(payload);
  }

  handleNoteOptionChange = ({ value }) => {
    const { notes: { data: notes } } = this.props;
    const notesExpanded = notes.reduce((acc, note) => {
      const noteExpandKey = generateNoteExpandKey(note.id);
      set(acc, noteExpandKey, value === 'expand');
      return acc;
    }, {});
    this.setState({
      notesExpanded,
      selectedNoteValue: null,
    });
  }

  handleNoteSortOptionChange = ({ value }) => {
    this.setState({ selectedNoteSortValue: value });
  }

  handleRecipientChange = (value) => {
    this.setState({
      selectedRecipients: value,
    });
  }

  handleReply = (note) => {
    const { selectedRecipients: oldSelectedRecipients } = this.state;
    const { currentUser } = this.props;

    const newRecipients = [note.sender, ...note.recipients].filter(recipient =>
      recipient.user.email !== currentUser.email,
    );
    const noteRecipients = buildRecipientOptions(newRecipients);
    const selectedRecipients = buildSelectedRecipients(oldSelectedRecipients, noteRecipients);
    this.setState({
      selectedRecipients,
      shouldFocusTextArea: true,
    });
    this.handleNewNoteClick();
  }

  handleStatusToggle = ({ id: noteId, status: oldStatus }) => {
    const status = oldStatus === 'read' ? 'unread' : 'read';
    this.handleUpdateNoteStatus(noteId, status);
  }

  handleUpdateNoteStatus = (noteId, status) => {
    this.props.updateNote({
      caseId: this.props.info.id,
      noteId,
      status,
    });
  }

  notesOptions = () => {
    const {
      i18n,
    } = this.props;
    return [
      {
        value: 'expand',
        label: i18n._(messages.expandAll),
      },
      {
        value: 'collapse',
        label: i18n._(messages.collapseAll),
      },
    ];
  }

  notesSortOptions = () => {
    const {
      i18n,
    } = this.props;
    return [
      {
        value: 'asc',
        label: i18n._(messages.oldToNew),
      },
      {
        value: 'desc',
        label: i18n._(messages.newToOld),
      },
    ];
  }

  scrollToNoteCreator = () => {
    const options = {
      duration: 1000,
      smooth: true,
      offset: -this.props.theme.dimensions.topNavHeightInPixel - 10, // adding some more space
    };

    if (this.props.type === 'modal') {
      options.containerId = 'modal-body';
    }

    scroller.scrollTo(SCROLLABLE_COMPOSER_NAME, options);
  }

  scrollToUnreadNote = () => {
    if (this.props.scrollToUnreadNote && this.props.notes.data.length) {
      const sortedNotes = sortNotes(
        this.props.notes.data,
        this.state.selectedNoteSortValue,
      );
      const firstUnreadNote = filterUnreadNotes(sortedNotes)[0];

      if (firstUnreadNote) {
        const options = {
          duration: 1500,
          smooth: true,
          delay: 200,
        };

        if (this.props.type === 'modal') {
          options.containerId = 'modal-body';
        } else {
          options.offset = -this.props.theme.dimensions.topNavHeightInPixel;
        }

        scroller.scrollTo(firstUnreadNote.id, options);
      }
      this.props.updateScrollToUnreadNote(false);
    }
  }

  render() {
    const {
      info,
      notes,
      availableRecipients,
    } = this.props;

    const {
      composerStatus,
      notesExpanded,
      selectedNoteSortValue,
      selectedNoteValue,
      selectedRecipients,
      oldNotesCollapsed,
    } = this.state;

    const notesOptions = this.notesOptions();
    const notesSortOptions = this.notesSortOptions();

    const selectedNoteOption = getCurrentOption(notesOptions, selectedNoteValue);
    const selectedNoteSortOption = getCurrentOption(notesSortOptions, selectedNoteSortValue);

    const sortedCaseNotes = sortNotes(notes.data, selectedNoteSortValue);
    const shouldCollapseNotes = sortedCaseNotes.length > 8;
    const ascendingOrder = selectedNoteSortValue === 'asc';
    let shownNotes;
    let collapsedNotes;
    if (shouldCollapseNotes) {
      if (ascendingOrder) {
        shownNotes = sortedCaseNotes.slice(-5);
        collapsedNotes = sortedCaseNotes.slice(0, -5);
      } else {
        shownNotes = sortedCaseNotes.slice(0, 5);
        collapsedNotes = sortedCaseNotes.slice(5);
      }
    } else {
      shownNotes = sortedCaseNotes;
      collapsedNotes = [];
    }
    const collapsedTitle = (
      <Container>
        <Row modifiers={['center']}>
          <Column modifiers={['col']}>
            <QuickActionButton
              onClick={this.handleExpandOldNotes}
            >
              <Trans
                id={messages.oldNotesCollapsedHeader}
                values={{ count: collapsedNotes.length }}
              />
            </QuickActionButton>
          </Column>
        </Row>
      </Container>
    );
    const collapsedNotesContent = (
      <Expandable
        isExpanded={!oldNotesCollapsed}
        showHeaderWhenExpanded={false}
        hasExpandIcon={false}
      >
        <ExpandableHeader>{collapsedTitle}</ExpandableHeader>
        <ExpandableContent>
          {
            collapsedNotes.map((note, index) => {
              const noteExpandKey = generateNoteExpandKey(note.id);
              const nextNoteIndex = index + 1 + (ascendingOrder ? 0 : shownNotes.length);
              const nextNote = sortedCaseNotes[nextNoteIndex];
              let borderBottom = false;
              if (nextNote && note.status === nextNote.status
                && note.status !== 'unread') {
                borderBottom = true;
              }
              return (
                <Note
                  key={note.id}
                  expandKey={noteExpandKey}
                  handleReply={this.handleReply}
                  handleStatusToggle={this.handleStatusToggle}
                  isExpanded={notesExpanded[noteExpandKey]}
                  note={note}
                  onExpand={this.handleExpandNote}
                  updateNoteStatus={this.handleUpdateNoteStatus}
                  borderBottom={borderBottom}
                />
              );
            })
          }
        </ExpandableContent>
      </Expandable>
    );

    // If notesCount is less than 9, show number of GhostNotes equal to notesCount
    // Otherwise, show 5
    const ghostNotesCount = info.notesCount < 9 ? info.notesCount : 5;
    const noNotesContent = (
      info.notesCount > 0
        ? (
          <div>
            {
              Array.from(new Array(ghostNotesCount), (x, i) => (
                <GhostNote key={i} />
              ))
            }
          </div>
        ) : (
          <div>
            <NoNotes requesting={notes.requesting} />
            <Divider modifiers={['light']} />
          </div>
        )
    );
    const shownNotesContent = (
      notes.data.length > 0
        ? (
          shownNotes.map((note, index) => {
            const noteExpandKey = generateNoteExpandKey(note.id);
            const nextNoteIndex = index + 1 + (ascendingOrder ? collapsedNotes.length : 0);
            const nextNote = sortedCaseNotes[nextNoteIndex];
            let borderBottom = false;
            if (nextNote && note.status === nextNote.status
              && note.status !== 'unread') {
              borderBottom = true;
            }
            return (
              <Note
                key={note.id}
                expandKey={noteExpandKey}
                handleReply={this.handleReply}
                handleStatusToggle={this.handleStatusToggle}
                isExpanded={notesExpanded[noteExpandKey]}
                note={note}
                onExpand={this.handleExpandNote}
                updateNoteStatus={this.handleUpdateNoteStatus}
                borderBottom={borderBottom}
              />
            );
          })
        ) : noNotesContent
    );

    const NotesWidgetBody = (
      <div>
        <NotesOptionsBar
          handleNewNoteClick={this.handleNewNoteClick}
          handleNoteOptionChange={this.handleNoteOptionChange}
          handleNoteSortOptionChange={this.handleNoteSortOptionChange}
          notesOptions={notesOptions}
          notesSortOptions={notesSortOptions}
          selectedNoteOption={selectedNoteOption}
          selectedNoteSortOption={selectedNoteSortOption}
        />
        {!ascendingOrder && shownNotesContent}
        {collapsedNotesContent}
        {ascendingOrder && shownNotesContent}
        <Container />
        <Element className="react-scroll-element NoteCreator" name={SCROLLABLE_COMPOSER_NAME}>
          <NoteCreator
            availableRecipients={availableRecipients}
            composerStatus={composerStatus}
            focusTextArea={this.focusTextArea}
            handleNewNoteCancel={this.handleNewNoteCancel}
            handleNewNoteClick={this.handleNewNoteClick}
            handleNoteCreate={this.handleNoteCreate}
            handleRecipientChange={this.handleRecipientChange}
            selectedRecipients={selectedRecipients}
          />
        </Element>
      </div>
    );

    if (this.props.type === 'modal') {
      return NotesWidgetBody;
    }

    return (
      <Widget className="NotesWidget" id="notes" expandKey="notes">
        <Widget.Header>
          <NotesWidgetHeader info={info} />
        </Widget.Header>
        <Widget.Item>
          {NotesWidgetBody}
        </Widget.Item>
      </Widget>
    );
  }
}

const NotesWidgetWithCommonHOCS = withTheme(withI18n()(NotesWidget));

export NotesWidgetHeader from './NotesWidgetHeader';
export const CaseNotesWidget = CaseNotesContainer(NotesWidgetWithCommonHOCS);
export const ServiceRequestNotesWidget = ServiceRequestNotesContainer(NotesWidgetWithCommonHOCS);



// WEBPACK FOOTER //
// ./compositions/NotesWidget/index.js